/**
 * Pioneering Projects Grid
 * 
 * Next-generation projects listing component with:
 * - Interactive 3D project cards
 * - Quick-view modal system
 * - Advanced filtering
 * - Animated stat visualizations
 * - Smooth grid animations
 */

'use client';

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import { Interactive3DProjectCard } from './Interactive3DProjectCard';
import { QuickViewModal } from './QuickViewModal';
import {
  AnimatedStatCard,
  CircularProgress,
  AnimatedComparisonChart,
  type ComparisonItem,
} from './InteractiveDataViz';

export interface PioneeringProjectsGridProps {
  projects: PropertyDevelopmentProject[];
  organisationId: string;
  locale?: string;
  messages: {
    // Grid messages
    title: string;
    subtitle: string;
    noProjects: string;
    noProjectsDescription: string;
    
    // Card messages
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
    explore: string;
    
    // Modal messages
    close: string;
    viewFullDetails: string;
    totalUnits: string;
    soldUnits: string;
    starting: string;
    completion: string;
    projectType: string;
    
    // Stats messages
    totalProjects: string;
    activeProjects: string;
    totalInvestment: string;
    soldPercentage: string;
  };
  brandColors?: {
    primary?: string;
    accent?: string;
  };
  className?: string;
}

export function PioneeringProjectsGrid({
  projects,
  organisationId: _organisationId,
  locale = 'en',
  messages,
  brandColors = {
    primary: '#243462',
    accent: '#BBA339',
  },
  className = '',
}: PioneeringProjectsGridProps) {
  const [quickViewProject, setQuickViewProject] = useState<PropertyDevelopmentProject | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Calculate stats
  const stats = useMemo(() => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter(p => p.status === 'ACTIVE').length;
    const totalUnits = projects.reduce((sum, p) => sum + (p.totalUnits || 0), 0);
    const soldUnits = projects.reduce((sum, p) => sum + (p.soldUnits || 0), 0);
    const soldPercentage = totalUnits > 0 ? Math.round((soldUnits / totalUnits) * 100) : 0;

    return {
      totalProjects,
      activeProjects,
      totalUnits,
      soldUnits,
      soldPercentage,
    };
  }, [projects]);

  // Prepare comparison data for chart
  const typeComparison: ComparisonItem[] = useMemo(() => {
    const typeCounts = projects.reduce((acc, project) => {
      const type = project.projectType || 'OTHER';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(typeCounts)
      .map(([label, value]) => ({
        label: label.replace(/_/g, ' '),
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [projects]);

  const handleQuickView = (e: React.MouseEvent, project: PropertyDevelopmentProject) => {
    e.preventDefault();
    setQuickViewProject(project);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hero Stats Section */}
      <motion.div
        className="relative mb-16 overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-black px-8 py-16 lg:px-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Title */}
        <div className="relative z-10 mb-12 text-center">
          <motion.h1
            className="mb-4 text-5xl font-bold text-white lg:text-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {messages.title}
          </motion.h1>
          <motion.p
            className="mx-auto max-w-3xl text-lg text-white/80 lg:text-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {messages.subtitle}
          </motion.p>
        </div>

        {/* Stats Dashboard */}
        <div className="relative z-10 grid grid-cols-2 gap-6 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <AnimatedStatCard
              value={stats.totalProjects}
              label={messages.totalProjects}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
              {...(brandColors.accent && { accentColor: brandColors.accent })}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <AnimatedStatCard
              value={stats.activeProjects}
              label={messages.activeProjects}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
              trend={{ value: 15, isPositive: true }}
              {...(brandColors.accent && { accentColor: brandColors.accent })}
            />
          </motion.div>

          <motion.div
            className="flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <CircularProgress
              percentage={stats.soldPercentage}
              {...(brandColors.accent && { primaryColor: brandColors.accent })}
              label={messages.soldPercentage}
              size={140}
              strokeWidth={10}
            />
          </motion.div>

          <motion.div
            className="col-span-2 rounded-2xl bg-white p-6 shadow-lg lg:col-span-1"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="mb-4 text-sm font-semibold text-gray-700">Project Types</h4>
            <AnimatedComparisonChart
              items={typeComparison.slice(0, 3)}
              {...(brandColors.accent && { accentColor: brandColors.accent })}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* View Mode Toggle */}
      <div className="mb-8 flex items-center justify-between">
        <motion.div
          className="text-sm text-gray-600"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          Showing <span className="font-bold">{projects.length}</span> projects
        </motion.div>

        <motion.div
          className="flex gap-2 rounded-lg bg-gray-100 p-1"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <button
            onClick={() => setViewMode('grid')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
              viewMode === 'grid'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </motion.div>
      </div>

      {/* Projects Grid */}
      <AnimatePresence mode="wait">
        {projects.length > 0 ? (
          <motion.div
            key={viewMode}
            className={`grid gap-8 ${
              viewMode === 'grid'
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                : 'grid-cols-1'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onContextMenu={(e) => handleQuickView(e, project)}
              >
                <div
                  onDoubleClick={(e) => handleQuickView(e, project)}
                  className="cursor-pointer"
                >
                  <Interactive3DProjectCard
                    project={project}
                    href={`/${locale}/projects/${project.slug || project.id}`}
                    brandColors={brandColors}
                    messages={{
                      ...messages,
                      explore: messages.explore || 'Explore',
                    }}
                    locale={locale}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="py-20 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-200">
              <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="mb-2 text-xl font-bold text-gray-900">{messages.noProjects}</h3>
            <p className="text-gray-600">{messages.noProjectsDescription}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick View Modal */}
      <QuickViewModal
        project={quickViewProject}
        isOpen={!!quickViewProject}
        onClose={() => setQuickViewProject(null)}
        messages={{
          close: messages.close,
          viewFullDetails: messages.viewFullDetails,
          availableUnits: messages.availableUnits,
          totalUnits: messages.totalUnits,
          soldUnits: messages.soldUnits,
          starting: messages.starting,
          completion: messages.completion,
          projectType: messages.projectType,
          enquireNow: messages.enquireNow,
        }}
        {...(brandColors.accent && { accentColor: brandColors.accent })}
        locale={locale}
      />
    </div>
  );
}

